import { Alert, Card, Form, Input, Space, Typography } from "antd";
import type { Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import type { PortWharfBerthPageParams, PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { PORT_WHARF_BERTH_TYPE } from "@zhihaoscm/meta";
import { BerthLocationModal } from "./BerthLocationModal";
import { useUserStore } from "@/stores/react/authStore";

export interface PortManagementQuery extends Omit<PortWharfBerthPageParams, "type"> {
    page: number;
    size: number;
    type: number;
}

export interface PortManagementPageProps {
    loadPorts?: (query: PortManagementQuery) => Promise<Page<PortWharfBerthVo>>;
    deletePort?: (id: number, type: number) => Promise<unknown>;
    permissions?: string[];
}

const defaultQuery: PortManagementQuery = {
    page: 1,
    size: 10,
    type: PORT_WHARF_BERTH_TYPE.HARBOR.code
};

/** 复用 Vue 港口管理列表的 `/port/paging` 接口和查询参数。 */
export async function loadPortManagement(query: PortManagementQuery): Promise<Page<PortWharfBerthVo>> {
    const response = await shippingRequestHttp.service.get<Page<PortWharfBerthVo>>("/port/paging", { params: query });
    return response.data;
}

/** 复用 Vue 港口删除接口，沿用 type=2 的港口数据契约。 */
export async function deletePortManagementFromList(id: number, type: number) {
    const response = await shippingRequestHttp.service.delete(`/port/${id}`, { params: { id, type } });
    return response.data;
}

/** 按旧页面分钟级格式展示更新时间，空值沿用后台占位文案。 */
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 保持 Vue 页面联系人字段的兼容展示规则。 */
const formatContact = (row: PortWharfBerthVo) => {
    const contactName = row.contactName?.trim();
    const phone = row.phone?.trim();
    if (!contactName && !phone) return DEFAULT_VALUE;
    if (contactName && phone) return `${contactName} ${phone}`;
    return contactName || phone || DEFAULT_VALUE;
};

/** 将 zhihao 表格的未知单元格值收敛为 React 可渲染的港口字段。 */
const formatCellValue = (value: unknown) =>
    typeof value === "string" || typeof value === "number" ? value : DEFAULT_VALUE;

/** 物流管理-港口管理列表，覆盖 Vue 首屏字段、关键词筛选与分页。 */
export function PortManagementPage({
    loadPorts = loadPortManagement,
    deletePort = deletePortManagementFromList,
    permissions
}: PortManagementPageProps) {
    const storePermissions = useUserStore(user => user.userInfo.permissions || []);
    const banButton = useUserStore(user => user.banButton);
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前登录用户权限。
    const canRead = (permissions ?? storePermissions).includes("ROLE_PORT_R");
    const canDeal = (permissions ?? storePermissions).includes("ROLE_PORT_DEAL");
    const canDelete = (permissions ?? storePermissions).includes("ROLE_PORT_W");
    const [rows, setRows] = useState<PortWharfBerthVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<PortManagementQuery>(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadPorts(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("港口列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadPorts, query]);

    // 将 Vue ProTable 的草稿筛选值转换成分页请求，并把筛选结果归一到第一页。
    const submitFilters = () => {
        const nextQuery: PortManagementQuery = { ...defaultQuery };
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        setQuery(nextQuery);
    };

    // 清空筛选并恢复港口管理列表首屏参数。
    const resetFilters = () => {
        setKeyword("");
        setQuery(defaultQuery);
    };

    // 删除成功后刷新当前港口列表，保留 Vue 列表当前筛选和分页状态。
    const handleDelete = useCallback(
        async (id: number) => {
            try {
                await deletePort(id, PORT_WHARF_BERTH_TYPE.HARBOR.code);
                const result = await loadPorts(query);
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            } catch {
                setErrorMessage("港口删除失败");
            }
        },
        [deletePort, loadPorts, query]
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 港口 ZhTable 的字段、排序和 operation 插槽。
    const portColumns = useMemo<TableColumnProps<PortWharfBerthVo>[]>(
        () => [
            {
                key: "index",
                label: "编号",
                prop: "id",
                render: ({ row, rowIndex }) => (
                    <Link to={`/logistics/portWharfBerth/portDetail?id=${row.id}`}>
                        {(query.page - 1) * query.size + rowIndex + 1}
                    </Link>
                )
            },
            { key: "name", label: "名称", prop: "name", render: ({ cellValue }) => formatCellValue(cellValue) },
            {
                key: "shortName",
                label: "简称",
                prop: "shortName",
                render: ({ cellValue }) => formatCellValue(cellValue)
            },
            {
                key: "fullAddress",
                label: "位置",
                prop: "fullAddress",
                render: ({ cellValue }) => formatCellValue(cellValue)
            },
            { key: "area", label: "面积", prop: "area", render: ({ cellValue }) => formatCellValue(cellValue) },
            {
                key: "harborCode",
                label: "港口代码",
                prop: "harborCode",
                render: ({ cellValue }) => formatCellValue(cellValue)
            },
            { key: "contact", label: "联系人信息", prop: "contactName", render: ({ row }) => formatContact(row) },
            {
                key: "updatedTime",
                label: "更新日期",
                prop: "updatedTime",
                sortable: true,
                render: ({ cellValue }) => formatDate(typeof cellValue === "string" ? cellValue : undefined)
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {canDeal ? (
                            banButton ? (
                                <Button disabled type="link">
                                    修改
                                </Button>
                            ) : (
                                <Link to={`/logistics/portWharfBerth/portEdit?id=${row.id}`}>修改</Link>
                            )
                        ) : null}
                        {canRead ? <BerthLocationModal berth={row} disabled={banButton} /> : null}
                        {canDelete ? (
                            <Button disabled={banButton} onClick={() => void handleDelete(Number(row.id))}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [banButton, canDeal, canDelete, canRead, handleDelete, query.page, query.size]
    );

    // 保留 Vue 港口列表对更新时间的 sortKey/sortOrder 请求契约。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "updatedTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = "updated_time";
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    return (
        <section aria-label="port-management-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<PortWharfBerthVo>
                    rowKey="id"
                    data={rows}
                    columns={portColumns}
                    emptyText="暂无港口"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    港口管理
                                </Typography.Title>
                                {canDeal ? (
                                    banButton ? (
                                        <Button disabled type="link">
                                            新增
                                        </Button>
                                    ) : (
                                        <Link to="/logistics/portWharfBerth/portAdd">新增</Link>
                                    )
                                ) : null}
                            </Space>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item label="港口名称/简称">
                                    <Input
                                        aria-label="港口名称/简称"
                                        value={keyword}
                                        onChange={event => setKeyword(event.target.value)}
                                        placeholder="请输入港口名称/简称关键词搜索"
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showSizeChanger={false}
                                onChange={page => setQuery(current => ({ ...current, page }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
